import useFormWithSchema from "../../../hooks/useFormWithSchema";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import TextInput from "../../../components/inputs/TextInput";
import ComboboxInput from "../../../components/inputs/ComboboxInput";
import {
  useGetAllSystemCodes,
  useGetAllSystemDetailCodes,
} from "../../../hooks/common/query";
import { mapToOptions } from "../../../lib/functions";
import SystemCodesTable from "../../../components/table/SystemCodesTable";
import { Button } from "../../../components/button";
import { useAddUpdateDetailCode } from "../../../hooks/user/mutation";
import toast, { Toaster } from "react-hot-toast";
import useAuthStore from "../../../store/Auth";
import { useGetDetailCodeById } from "../../../hooks/user/query";
import { SystemCodesSchema } from "../../../schemas/systemCodesSchema";
import { PAGE_SIZE } from "../../../constants/common";
import BackButtonWrapper from "../../../components/button/BackButton/wrapper";

const SystemCodes = () => {
  const { t } = useTranslation();

  const [page, setPage] = useState<number>(1);
  const [pages, setPages] = useState(1);
  const [count, setCount] = useState(0);
  const [search, setSearch] = useState("");
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZE);

  const [allSystemDetailCodes, setAllSystemDetailCodes] = useState([]);

  const user = useAuthStore((state) => state.user);

  const {
    control,
    formState: { errors },
    watch,
    handleSubmit,
    resetField,
    setValue,
  } = useFormWithSchema(SystemCodesSchema);

  const masterCode = watch("masterCode");

  const { data: allSystemCodes } = useGetAllSystemCodes();
  const { data: detailCodes } = useGetAllSystemDetailCodes({
    ...(masterCode && { encryptedId: masterCode.toString() }),
    pageNumber: page,
    pageSize: pageSize,
    search: search,
  });

  const [selectedCodeId, setSelectedCodeId] = useState(0);

  const {
    mutateAsync: AddUpdateDetailCode,
    isPending: isAddUpdateDetailCodeLoading,
  } = useAddUpdateDetailCode();

  const { data: detailCode } = useGetDetailCodeById({
    id: selectedCodeId,
  });

  const systemCodes = useMemo(
    () => mapToOptions("s_Desc_E", "s_Desc_A", "n_MasterCode", allSystemCodes),
    [allSystemCodes]
  );

  useEffect(() => {
    if (detailCodes) {
      setAllSystemDetailCodes(detailCodes.data || []);
      setPages(detailCodes.pagination?.pages || 1);
      setCount(detailCodes.pagination?.count || 0);

      resetField("arabicDesc");
      resetField("engDesc");
      setSelectedCodeId(0);
    }
  }, [setPages, detailCodes, resetField]);

  useEffect(() => {
    if (detailCode) {
      setValue("engDesc", detailCode.s_Desc_E);
      setValue("arabicDesc", detailCode.s_Desc_A);
    }
  }, [detailCode, setValue]);

  const onSubmit = useCallback(
    async (data: any) => {
      try {
        const body = {
          N_DetailedCode: detailCode ? detailCode?.n_DetailedCode : 0,
          N_MasterCode: Number(masterCode),
          S_Desc_A: data.arabicDesc,
          S_Desc_E: data.engDesc,
          N_CreatedBy: user?.user_id,
          N_LocationID: user?.n_LocationID,
        };

        await AddUpdateDetailCode(body);

        setSelectedCodeId(0);

        toast.success(
          !detailCode ? t("detailCodeAdded") : t("detailCodeUpdated")
        );

        resetField("arabicDesc");
        resetField("engDesc");
      } catch (error) {
        toast.error(t("somethingWentWrong"));
      }
    },
    [
      AddUpdateDetailCode,
      detailCode,
      masterCode,
      resetField,
      user?.n_LocationID,
      user?.user_id,
      t,
    ]
  );

  const onClear = useCallback(() => {
    resetField("arabicDesc");
    resetField("engDesc");
  }, [resetField]);

  return (
    <>
      <Toaster position="top-right" reverseOrder={false} />

      <div className="flex flex-col gap-4 md:gap-8">
        <BackButtonWrapper>
          <h1 className="text-xl md:text-2xl dark:text-white">
            {t("systemCodes")}
          </h1>
        </BackButtonWrapper>

        <form className="flex flex-col gap-4 md:gap-8">
          <div className="bg-white p-4 rounded-xl flex flex-col gap-3 lg:gap-6 shadow-[0px_1px_5px_0px_rgba(113,112,163,0.3)]">
            <div className="items-end gap-3 grid md:grid-cols-2 xl:grid-cols-3">
              <Controller
                name="masterCode"
                control={control}
                render={({ field: { onChange, name, value } }) => {
                  return (
                    <ComboboxInput
                      label={t("masterCode")}
                      options={systemCodes}
                      value={
                        systemCodes?.find((item) => item.value === value)
                          ?.labelEn || ""
                      }
                      onChange={(value) => {
                        onChange(value);
                        onClear();
                        setPage(1);
                      }}
                      error={errors?.[name]?.message}
                      required
                    />
                  );
                }}
              />
            </div>
          </div>

          {masterCode && (
            <div className="bg-white p-4 rounded-xl flex flex-col gap-3 lg:gap-6 shadow-[0px_1px_5px_0px_rgba(113,112,163,0.3)]">
              <SystemCodesTable
                allDetailCodes={allSystemDetailCodes}
                page={page}
                pages={pages}
                setPage={setPage}
                search={search}
                setSearch={setSearch}
                count={count}
                setSelectedCodeId={setSelectedCodeId}
                pageSize={pageSize}
                setPageSize={setPageSize}
              />

              <div className="flex flex-col md:flex-row gap-4 md:items-end">
                <div className="w-full">
                  <Controller
                    name="engDesc"
                    control={control}
                    render={({ field: { onChange, name, value } }) => (
                      <TextInput
                        label={t("descriptionEn")}
                        onChange={onChange}
                        value={value || ""}
                        error={errors?.[name]?.message}
                        required
                        className="w-full"
                      />
                    )}
                  />
                </div>
                <div className="w-full">
                  <Controller
                    name="arabicDesc"
                    control={control}
                    render={({ field: { onChange, name, value } }) => (
                      <TextInput
                        label={t("descriptionAr")}
                        onChange={onChange}
                        value={value || ""}
                        required
                        error={errors?.[name]?.message}
                        className="w-full"
                      />
                    )}
                  />
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    onClick={handleSubmit(onSubmit)}
                    className="py-[14px] px-10"
                    isLoading={isAddUpdateDetailCodeLoading}
                  >
                    {t("save")}
                  </Button>
                  <Button
                    type="button"
                    onClick={onClear}
                    className="py-[14px] px-9 bg-transparent ring-1 ring-purple-primary text-purple-primary hover:bg-transparent"
                  >
                    {t("clear")}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </form>
      </div>
    </>
  );
};

export default SystemCodes;
